import { AppCopyText, AppDialog, AppPageHeader, AppSectionErrorBoundary, AppSpacer, AppUseIcon } from 'components/ui';
import { OrganizationsTableColumns, appTableFiltersInstance } from './OrganizationsTableColumns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppButton } from 'components/form';
import { DefaultRoutes } from 'Routes';
import { IOrganization } from 'types/models/OrganizationModels';
import { OrganizationFormDialog } from './components/OrganizationFormDialog';
import { StaticTableWithFilters } from 'components/Tables/StaticTableWithFilters';
import { TableCell } from '@trackman/web-shared-components';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';

export const OrganizationsPage: React.FunctionComponent = observer(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const { organizationsStore } = useStores();
  const [isOrganizationDialogOpened, setIsOrganizationDialogOpened] = useState<boolean>(false);

  useEffect(() => {
    organizationsStore.getAllOrganizations({ pageNumber: 1, take: 10 });
    organizationsStore.getAllLicenses();
  }, []);

  const rows = useMemo(() => organizationsStore.organizations, [organizationsStore.organizations]);

  const columns = useMemo(() => OrganizationsTableColumns(organizationsStore.licenses.map((v) => v.key)), [organizationsStore.licenses]);

  const renderCell = useCallback(
    ({ cellValue, columnHeader, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {
      if (columnId === 'id') {
        return (
          <div className='d-flex justify-content-between align-items-center w-100'>
            <p data-test={`id-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
              {cellValue}
            </p>
            <AppCopyText className='ml-1 ' text={cellValue} />
          </div>
        );
      }

      if (columnId === 'name' || columnId === 'shortName') {
        return (
          <span className='overflow-ellipsis text-capitalize' title={`${cellValue?.trim() || '-'}`}>
            {cellValue?.trim() || '-'}
          </span>
        );
      }

      if (columnId === 'licenses') {
        return (
          <span className='overflow-ellipsis text-capitalize' title={`${cellValue?.join(', ').trim() || '-'}`}>
            {cellValue?.join(', ').trim() || '-'}
          </span>
        );
      }

      if (columnId === 'actions') {
        return (
          <div className='d-flex'>
            <AppButton
              variant='link'
              className='color-textElementGrey text-underline px-0'
              onClick={(e) => {
                e?.stopPropagation();
                history.push(`${DefaultRoutes.UsersPage}/${row.id}`);
              }}
            >
              {t('users')}
            </AppButton>
            <AppSpacer space='20px' isHorizontal />
            <AppButton
              variant='link'
              className='color-textElementGrey text-underline px-0'
              onClick={(e) => {
                e?.stopPropagation();
                handleSelectingUserDiagnostic(row);
              }}
            >
              {t('UserDiagnostics.Title')}
            </AppButton>
            <AppSpacer space='20px' isHorizontal />
            <AppButton
              variant='link'
              className='color-textElementGrey text-underline px-0'
              onClick={(e) => {
                e?.stopPropagation();
                handleSelectingTrackingDiagnostic(row);
              }}
            >
              {t('TrackingDiagnostics.Title')}
            </AppButton>
            <AppSpacer space='20px' isHorizontal />
            <AppButton
              variant='link'
              className='color-textElementGrey text-underline px-0'
              onClick={(e) => {
                e?.stopPropagation();
                handleSelectingOrganization(row as IOrganization);
              }}
            >
              {t('edit')}
            </AppButton>
          </div>
        );
      }

      return cellValue || '-';
    },
    []
  );

  const handleSelectingOrganization = useCallback((organization: IOrganization) => {
    organizationsStore.setSelectedOrganization(organization);
    setIsOrganizationDialogOpened(true);
  }, []);

  const handleSelectingUserDiagnostic = useCallback((organization: IOrganization) => {
    history.push({
      pathname: `${DefaultRoutes.UserDiagnosticsPage}/`,
      search: `?organizationName=${organization.name}`
    });
  }, []);

  const handleSelectingTrackingDiagnostic = useCallback((organization: IOrganization) => {
    history.push({
      pathname: `${DefaultRoutes.TrackingDiagnosticsPage}/`,
      search: `?organizationName=${organization.name}`
    });
  }, []);

  const rowMapper = useCallback((cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell {...getCellProps()} align={column.align} onClick={() => history.push(`${DefaultRoutes.UsersPage}/${row.original.id}`)}>
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
          </TableCell>
        ))}
      </>
    );
  }, []);

  const handleOpeningOrganizationDialog = useCallback(() => {
    setIsOrganizationDialogOpened(true);
  }, []);

  const handleClosingOrganizationDialog = useCallback(() => {
    setIsOrganizationDialogOpened(false);
  }, []);

  useEffect(() => {
    if (!isOrganizationDialogOpened && organizationsStore.selectedOrganization) {
      organizationsStore.setSelectedOrganization(null);
    }
  }, [isOrganizationDialogOpened]);

  return (
    <AppSectionErrorBoundary>
      <div className='organizations-tables'>
        <AppPageHeader title={t('organizations')}>
          <>
            <AppButton
              className='position-absolute right-0 mt-auto mb-auto'
              variant='transparent'
              data-test='add-organization-button'
              id='add-organization-button'
              disabled={organizationsStore.isLoading}
              onClick={handleOpeningOrganizationDialog}
            >
              {t('addOrganization')}
              <AppUseIcon name='PlusIconWithoutBorder' width='14' height='14' className='ml-2' />
            </AppButton>
          </>
        </AppPageHeader>

        <AppSpacer space='24px' />

        <StaticTableWithFilters<IOrganization>
          appTableFiltersInstance={appTableFiltersInstance}
          rowMapper={rowMapper}
          columns={columns}
          data={rows}
          isLoading={organizationsStore.isLoading}
          disableResettingPaginationOnUpdate={true}
        />

        <OrganizationFormDialog
          isOpened={isOrganizationDialogOpened}
          onClose={handleClosingOrganizationDialog}
          organization={organizationsStore.selectedOrganization}
        />
      </div>
    </AppSectionErrorBoundary>
  );
});

export default OrganizationsPage;
